/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React from 'react';
import * as mobx from 'mobx';
import { types, getEnv } from 'mobx-state-tree';
import { replaceArray, appendToArray, deleteFromArray } from 'util/ArrayOperations';
import Config from './Config';
import { ScopeModel } from './ScopeModel';
import { FULFILLED, REJECTED } from 'mobx-utils';
import { showRequestFinishedToast } from 'util/ToastUtil';
import Logger from 'util/Logger';


export const OVERRIDE_ACTIONS = {
	ADDED: 'added',
	DELETED: 'deleted'
};

/**
 * Some information are associated with a certain target ID, for instance the
 * array of addded files to diagram, an array of removed fields from diagram,
 * etc.
 */
export default types
	.model('RelationshipTargetModel', {
	})
	.volatile(()=>({
		addedFields: mobx.observable([]), /*Added columns to an existing relationship diagram*/
		deletedFields: mobx.observable([]), /*Deleted columsn from an existing relationship diagram*/
		lastAddedFields: null, /*last added fields*/
		lastCanceledAction: null,
		diagramData: null, /*diagram data after transformations */
		initialState: true, /* as long as user doesn't change the filter strength, the diagram should stay in this state, that means limited to 10 nodes */
		showPrimaryRelationshipView: Config.default_show_primary_relationship_view, /*true by default*/
		filterStrength: {
			min: Config.default_min_strength,
			max: Config.default_max_strength
		}, /*min/max relationship strength set by user */
		initialFilterStrength: {
			min: Config.default_min_strength,
			max: Config.default_max_strength
		}, /*min/max relationship strength calculated from initial loading of a target*/
		scope: null,
		renderNeeded: 0, /*indicator to trigger fetchData on selective property change*/
		changedProperty: '', /*the property that is modified and need fetch data*/
		actionStatus: null, /*A JSON that shows the current status of the actions { level: xxx, message: xxx}*/
		showRelationshipsMap: {},
		lastShowRelationshipsField: {},
		data : null,/*a FromPromise instance for the relationship fetch data request*/
		id: null,/*TargetModel's Id is the tareget column ID*/
		requestStartTime: null,/*Starting Time of a Relationship fecth request*/
		requestEndTime: null,/*Starting Time of a Relationship fecth request*/
		abandon: null, /*delegator (function) to abadon a pending fetch data promise*/
		metadataInfo: null/*Metadata information for all columns*/
	}))
	.views((self) => ({
		get computedMinStrength() {
			if (self.initialState) {
				return self.initialFilterStrength.min;
			} else {
				return self.filterStrength.min;
			}
		},

		get computedMaxStrength() {
			if (self.initialState) {
				return self.initialFilterStrength.max;
			} else {
				return self.filterStrength.max;
			}
		},

		/**
		* This function based on the modified property to return the type of transformation and its parameters that need to pass to the relationshipStrengthService via function fetchData to filter data before rendering
		* (e.g primary/secondary view, filter strength, delete/add operation, etc...)
		*/
		getTransformOptions() {
			let transformOptions = null;
			switch(self.changedProperty) {
				case 'filterStrength':
				case 'showPrimaryRelationshipView':
				case 'cancelAction':
					transformOptions = {
						'filterStrength': self.filterStrength
					};
					break;
				case 'deletedFields':
					transformOptions = {
						'deletedFields': self.deletedFields
					};
					break;
				case 'lastAddedFields':
					transformOptions = {
						'lastAddedFields' : self.lastAddedFields,
						'addedFields': self.addedFields,
						'showPrimaryRelationshipView': self.showPrimaryRelationshipView,
						'targetId': self.id
					};
					break;
				case 'lastShowRelationshipsField':
					transformOptions = {
						'lastShowRelationshipsField': self.lastShowRelationshipsField
					};
					break;
				case 'scopedFields':
					transformOptions = {
						scopedFields: self.scope.getScopedFields().map(scopedField => scopedField.fieldIDForExpression),
						targetId: self.id
					};
					break;
				default:
					return null;
			}
			return {
				transformOptions: transformOptions
			};
		},

		getDiagramData() {
			return self.diagramData;
		},

		getInitialState() {
			return self.initialState;
		},

		getDiagramFilterCondition() {
			const maxNodesToDisplay = self.initialState ? Config.max_nodes_to_firstTime_display : Config.max_nodes_to_display;
			return {
				'targetId': self.id,
				'showPrimaryRelationshipView': self.showPrimaryRelationshipView,
				'maxNodesToDisplay': maxNodesToDisplay,
				'maxLinksToDisplay': Math.round(maxNodesToDisplay * Config.node_Link_ratio),
				'filterStrength': self.initialState ? null : self.filterStrength,
				'deletedFields': self.deletedFields,
				'addedFields': self.addedFields
			};
		},

		getDeletedFields() {
			return self.deletedFields;
		},

		getAddedFields() {
			return self.addedFields;
		},

		getScope() {
			return self.scope;
		},

		getShowRelationships() {
			return self.showRelationshipsMap;
		},

		/*Return the current Id of the target model*/
		getTargetId(){
			return self.id;
		},

		getDiagramNodeCount() {
			const diagramData = self.getDiagramData();
			if (diagramData && diagramData.nodesDataset && diagramData.nodesDataset.groupings && diagramData.nodesDataset.groupings.length && diagramData.nodesDataset.groupings[0].groups) {
				return diagramData.nodesDataset.groupings[0].groups.length;
			}
			return 0;
		},

		/**
		 * see if we need to change the fetchDataContext. eg for the case that changing back to an abandoned target,
		 * if the abandonedrequest  is a editing scope request, we want to resend a editing scope request (ie has analysisScope)
		 * instead of sending a common request
		 * @param {object} fetchDataContext
		 */
		modifyFetchDataContextIfNeeded(fetchDataContext) {
			if (self.data && self.data.state === REJECTED) {
				const error = self.data.value;
				if(error) {
					if (error.errObj && error.errObj.code === 'CA_EXPLORE_REL_003'/* abandoned request error, see StartingPointsRequestError*/) {
						// if previously abandoned request is a edting scope request
						if (error.abandonedRequest === 'scopedFields') {
							// and if we are changing target, we need to resend the editing scope request
							if (fetchDataContext && fetchDataContext.setTarget === true) {
								const scopedFields = self.scope.getScopedFields();
								if(scopedFields) {
									fetchDataContext.transformOptions = {
										scopedFields: scopedFields.map(scopedField => scopedField.fieldIDForExpression),
										targetId: self.id
									};
								}
							}
						}
					}
				}
			}
			return fetchDataContext;
		}
	}))
	.actions((self) => {
		const disposeFunctions = [];
		return {
			afterCreate() {
				self.logger = new Logger('CA_EXPLORE_RELATIONSHIP_TARGETMODEL_VM');//explore natvie logger
				self.relationshipVM = getEnv(self).relationshipVM;
				self.id = getEnv(self).id;
				self.label = getEnv(self).label;
				const dataSourceId = self.relationshipVM.dataSourceId;
				const fieldId = self.id;
				const module = getEnv(self).exploreVM.activeDataSourceModule;
				const sourceModuleId = module && module.value.getAssetId();

				self.scope = ScopeModel.create({
					fieldId: fieldId,
					dataSourceId: dataSourceId,
					sourceModuleId: sourceModuleId
				}, { targetModel: self, exploreVM: getEnv(self).exploreVM });

				disposeFunctions.push(
					mobx.reaction(() => {
						return self.renderNeeded;
					},()=>{
						const fetchDataContext = self.getTransformOptions();
						if (fetchDataContext) {
							self.relationshipVM.fetchData(fetchDataContext);
						}
					})
				);

				disposeFunctions.push(
					mobx.reaction(() => {
						return self.data && self.data.state;
					},(state)=>{
						//Allways show toast message if the response data is not for the current targetID,
						//Allways Show toast message if the current explore perspective is deactivated
						//Only show toast message if the request is a long request, check Config.request_execution_time_elapse_to_show_fulfilled_toast
						if(state === FULFILLED && self._shouldShowToast()){
							showRequestFinishedToast(self.relationshipVM.glassContext, self, self.relationshipVM.exploreViewId);
						}
					})
				);
			},

			_shouldShowToast(){
				self.stopRequestTimer();
				const isLongRequest = self.getRequsetTimeDuration() >= Config.request_execution_time_elapse_to_show_fulfilled_toast;
				if(isLongRequest){
					if(self.relationshipVM.exploreViewActivated){
						return self.relationshipVM.getCurrentTargetId() !== self.id;
					}else{
						return true;
					}
				}
			},

			setChangedProperty(propertyName) {
				self.changedProperty = propertyName;
			},

			/**
			 * Build the analysisScope query parameter for /relationship request
			 * @param  {string[]} fieldsArray A flat list of fields IDs.
			 * @return {object}
			 * @example
			 * 		[
						 {
							 “tableID” : “Hollywood_Movies_csv”,
							 “inScopeFieldIDs” : [ “Title”, “Year”, “Release_Day” ]
						 } ,
						 {
							 “tableID” : “Anohter table ID”,
							 “inScopeFieldIDs” : [ “Field1”, “Field2”, “Field3” ]
						 }
			 	 	]
			 *
			 */
			buildScopedFields(fieldsArray){
				//the fieldID should be composed of query subject ID + . + columndID
				//we are splitting them using the last ., a known limitation is that
				//this only support relational datasource.
				const result = [];
				let querySubjectID;
				let columnID;
				let length;
				let lastDotPos;
				const targetId = self.id;
				fieldsArray.forEach( (fieldID)=>{
					// Do not add targetId in the list of analysis scope, server throws duplicate node in this case
					if (fieldID !== targetId) {
						length = fieldID.length;
						lastDotPos = fieldID.lastIndexOf('.');
						querySubjectID = fieldID.substr(0, lastDotPos);
						columnID = fieldID.substr(lastDotPos + 1, length);
						const tableEntry = result.find((entry)=>entry.tableID == querySubjectID);
						if(!tableEntry){
							result.push(
								{
									'tableID' : querySubjectID,
									'inScopeFieldIDs' : [ columnID ]
								} ,
							);
						}else{
							tableEntry.inScopeFieldIDs.push(columnID);
						}
					}
				});
				return result;
			},


			/**
			 * Reset model to initial state,
			 * such as empty added fields, empty removed fields
			 */
			reset() {
				self.resetTargetModelProps();
				self.resetScope();
				//rerender the diagram
				self.renderNeeded++;
				self.setChangedProperty('filterStrength');
			},

			resetTargetModelProps() {
				self.setInitialState(true);
				replaceArray(self.addedFields, []);
				replaceArray(self.deletedFields,[]);
				self.showRelationshipsMap = {};
				self.lastShowRelationshipsField = {};
				self.lastAddedFields = null;
				self.filterStrength.min = Config.default_min_strength;
				self.filterStrength.max = Config.default_max_strength;
				self.showPrimaryRelationshipView = Config.default_show_primary_relationship_view;
				self.setDiagramData(null);
			},

			resetScope() {
				self.scope && self.scope.reset();
			},

			/**
			 * Set Scope
			 * @param {object[]} fields have data structure like below
					[
						{
							fieldID: '',
							fieldIDForExpression: '',
							tableID: '',
						},
						{
							fieldID: '',
							fieldIDForExpression: '',
							tableID: '',
						}
					]
			 */
			setScope(fields) {
				if(fields) {
					const isSetNewScope = self.scope && self.scope.setScopedFields(fields);
					if(isSetNewScope) {
						// when change to a new scope, reset the filter
						if(!self.initialState) {
							self.setInitialState(true);
						}
						const scopedFields = self.scope.getScopedFields();
						if(scopedFields) {
							// when a field is no longer in the new scope, then remove it from both `added` and `deleted`
							const foundInScopedFields = field => scopedFields.find(scopedField => scopedField.fieldIDForExpression === field);
							replaceArray(self.addedFields, self.addedFields.filter(foundInScopedFields));
							replaceArray(self.deletedFields, self.deletedFields.filter(foundInScopedFields));
							//update showRelationshipsMap
							const froms = Object.keys(self.showRelationshipsMap);
							if (froms.length) {
								froms.forEach((from) => {
									if(!foundInScopedFields(from)) {
										delete self.showRelationshipsMap[from];
										return;
									}
									const toEntries = self.showRelationshipsMap[from];
									replaceArray(toEntries, toEntries.filter(toEntry => foundInScopedFields(toEntry.to)));
									if(toEntries.length === 0) {
										delete self.showRelationshipsMap[from];
									}
								});
							}
							// fetch data for new scope
							self.renderNeeded++;
							self.setChangedProperty('scopedFields');
						}
					}
				}
			},

			/**
			 * Set last added fields
			 * @param {String[]} columnIds, metadata column IDs
			 */
			setLastAddedFields(columnIds){
				self.lastAddedFields = columnIds;
				self.addToAddedFields(columnIds);
				self.scope && self.scope.addToScopedFields(columnIds);
				self.renderNeeded++;
				self.setChangedProperty('lastAddedFields');
			},

			/**
			 * Add added field Ids to the added fields list
			 * @param {String[]} columnIds
			 */
			addToAddedFields(columnIds) {
				if(columnIds && columnIds.length) {
					//Need to remove the columns just added from the deleted fields
					replaceArray(self.deletedFields, self.deletedFields.filter((deletedField => columnIds.indexOf(deletedField) === -1)));
					columnIds.forEach((columnId) => {
						if (self.addedFields && self.addedFields.length) {
							if (self.addedFields.indexOf(columnId) === -1) {
								self.addedFields.push(columnId);
							}
						} else {
							self.addedFields = [columnId];
						}
					});
				}
			},

			/**
			 * Set a fromPromise object to targetModel
			 * @param {object} data fromProise object
			 */
			setData(data){
				self.data = data;
			},

			/**
			 * Add removed field Ids to the removed fields list
			 * @param {String[]} columnIds
			 */
			deleteFields(columnIds) {
				//Need to remove the columns just deleted from the added fields
				replaceArray(self.addedFields, self.addedFields.filter((addedField => columnIds.indexOf(addedField) === -1)));
				//Will also need to clear showRelationshipsMap
				columnIds.forEach((columnId) => self.deleteFromShowRelationshipsMap(columnId));
				appendToArray(self.deletedFields, columnIds);
				self.renderNeeded++;
				self.setChangedProperty('deletedFields');
			},

			beforeDestroy() {
				disposeFunctions.forEach(dispose => dispose());
			},

			setFilterStrength(filterStrengthValue) {
				// User has changed filter strength, unset initial state
				self.setInitialState(false);
				self.filterStrength.min = filterStrengthValue.min;
				self.filterStrength.max = filterStrengthValue.max;
				self.renderNeeded++;
				self.setChangedProperty('filterStrength');
			},

			setInitialFilterStrength(filterStrengthValue) {
				self.initialFilterStrength.min = filterStrengthValue.min;
				self.initialFilterStrength.max = filterStrengthValue.max;
			},

			setShowPrimaryRelationshipView(value){
				self.showPrimaryRelationshipView = value;
				//when changing mode, set initialState to false to not limit the number of displayed nodes to 10
				if(self.initialState) {
					self.setInitialState(false);
				}
				self.renderNeeded++;
				self.setChangedProperty('showPrimaryRelationshipView');
			},

			setDiagramData(data) {
				self.diagramData = data;
			},

			setInitialState(value) {
				self.initialState = value;
			},

			setActionErrorStatus(message){
				self._setActionStatus ('error', message);
			},

			setActionWarnStatus(message){
				self._setActionStatus ('warning', message);
			},

			setActionInfoStatus(message){
				self._setActionStatus ('info', message);
			},

			setActionSuccessStatus(message){
				self._setActionStatus ('success', message);
			},

			_setActionStatus(level, message){
				self.actionStatus = {
					level: level,
					message: message
				};
			},

			emptyActionStatus(){
				self.actionStatus = null;
			},

			updateInitialFilterStrength (filterStrengthInfo) {
				if (self.initialState && filterStrengthInfo) {
					self.setInitialFilterStrength(filterStrengthInfo);
					//keep filterStrength in sync with InitialFilterStrength
					self.filterStrength.min = filterStrengthInfo.min;
					self.filterStrength.max = filterStrengthInfo.max;
				}
			},

			/**
			 * Show the relationship between `from` node and `to` node
			 * @param {string} from - field id
			 * @param {string} to - field id
			 */
			showRelationshipsBetween(from, to) {
				replaceArray(self.deletedFields, self.deletedFields.filter((deletedField => deletedField !== to)));
				if(self.scope) {
					const scopedFields = self.scope.getScopedFields();
					const targetId = self.id;
					if(scopedFields) {
						if (to !== targetId && (!scopedFields.find(scopedField => scopedField.fieldIDForExpression === to))) {
							self.scope.addToScopedFields([to]);
						}
					}
				}
				if(self.showRelationshipsMap[from]) {
					if(!self.showRelationshipsMap[from].find((entry) => entry.to === to)) {
						self.showRelationshipsMap[from].push({ to });
					}
				} else {
					self.showRelationshipsMap[from] = [{ to }];
				}
				self.lastShowRelationshipsField = { from, to };
				self.renderNeeded++;
				self.setChangedProperty('lastShowRelationshipsField');
			},

			/**
			 * Set the relationship bewtween `from` field and `to` field
			 * @param {*} from - `from` field
			 * @param {*} to - `to` field
			 * @param {*} relationship - relationship bewtween `from` field and `to` field: {toNode, link}
			 */
			setRelationship(from, to, relationship) {
				if(self.showRelationshipsMap[from]) {
					const found = self.showRelationshipsMap[from].find((entry) => entry.to === to);
					if(found) {
						found.relationship = relationship;
					}
				}
			},

			/**
			 * Set the request starting time stamp
			 */
			startRequestTimer(){
				self.resetRequestTimer();
				self.requestStartTime = new Date();
			},

			/**
			 * Set the request ending time stamp
			 */
			stopRequestTimer(){
				self.requestEndTime = new Date();
			},

			/**
			 * Get the request duration time
			 */
			getRequsetTimeDuration(){
				return self.requestEndTime && self.requestStartTime ? self.requestEndTime.getTime() - self.requestStartTime.getTime(): 0 ;//Ms
			},

			/**
			 * Reset the request staring and ending time stamp
			 */
			resetRequestTimer(){
				self.requestStartTime = null;
				self.requestEndTime = null;
			},

			/**
			 * Given fieldId, delete it from the `ShowRelationshipsMap`
			 * @param {string} columnId - field id
			 */
			deleteFromShowRelationshipsMap(columnId) {
				//delete from `from fields`
				if(self.showRelationshipsMap[columnId]) {
					delete self.showRelationshipsMap[columnId];
				}

				//delete from `to fields`
				for(const from in self.showRelationshipsMap) {
					const toEntries = self.showRelationshipsMap[from];
					deleteFromArray(toEntries, (toEntry) => toEntry.to === columnId);
					if(toEntries.length === 0) {
						delete self.showRelationshipsMap[from];
					}
				}
			},

			/**
			 * Cancel an action
			 * @param {string} actionToCancel - one of actions in `OVERRIDE_ACTIONS`
			 * @param {string} fieldID - field id (the field that the action to be canceled on)
			 */
			cancelAction(actionToCancel, fieldID) {
				switch(actionToCancel) {
					case OVERRIDE_ACTIONS.ADDED: {
						deleteFromArray(self.addedFields, (addedField) => addedField === fieldID);
						self.deleteFromShowRelationshipsMap(fieldID);
						self.lastCanceledAction = {
							canceledAction: OVERRIDE_ACTIONS.ADDED,
							fieldID
						};
						break;
					}
					case OVERRIDE_ACTIONS.DELETED: {
						deleteFromArray(self.deletedFields, (deletedField) => deletedField === fieldID);
						self.lastCanceledAction = {
							canceledAction: OVERRIDE_ACTIONS.DELETED,
							fieldID
						};
						break;
					}
					default:
						return;
				}
				self.setChangedProperty('cancelAction');
				self.renderNeeded++;
			},
			/**
			 * Set the abadon request trigger function
			 * //TODO: Move the trigger to a fromPromise
			 * @param {function}
			 */
			setAbandon(abandon) {
				self.abandon = abandon;
			},

			/**
			 * abandon Request
			 */
			abandonRequest() {
				if (self.abandon) {
					self.abandon();
				}
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/RelationshipTargetModel.js